/**
 * `aethereum share <file>` / `aethereum fetch <ref>` — the CLI surface for G2 E2EE
 * code sharing. Encryption happens HERE, on your machine, before anything leaves it;
 * the hosted rail stores and serves only ciphertext it cannot read.
 *
 * These resolve the room from the project config (url + token + roomId) and drive
 * `HostedCodeClient`, which holds the keystore and does the crypto. Kept deliberately
 * thin: all the security lives in `code-share.ts` / `e2ee.ts` / `hosted-code-client.ts`.
 */

import { readFileSync, writeFileSync } from "node:fs";
import { relative, isAbsolute, basename } from "node:path";
import { verify as verifyMod, scanForInjection, injectionWarning } from "@br9704/aethereum-shared";
import { resolveProject } from "../hive/config-store.js";
import { HostedCodeClient } from "../hive/hosted-code-client.js";
import { loadVerifyStore, saveVerifyStore } from "../hive/verify-store.js";
import { validRosterEntry } from "../hive/code-share.js";

const unb64 = (s: string): Uint8Array => new Uint8Array(Buffer.from(s, "base64"));

/** Injectable prompt/TTY deps so tests can drive the consent flow deterministically. */
export interface ShareConsentDeps {
  isTTY?: boolean;
  confirm?: (question: string) => Promise<boolean>;
  out?: (line: string) => void;
  err?: (line: string) => void;
}

async function readlineConfirm(question: string): Promise<boolean> {
  const { createInterface } = await import("node:readline/promises");
  const rl = createInterface({ input: process.stdin, output: process.stdout });
  try {
    const answer = (await rl.question(question)).trim().toLowerCase();
    return answer === "y" || answer === "yes";
  } finally {
    rl.close();
  }
}

/**
 * v6-S1: the first-contact consent hook. Interactive TTY: loud banner listing the
 * unverified recipients (with short key fingerprints) + an explicit confirm.
 * Non-interactive (hooks, CI, MCP runtime): warn + proceed — fail-soft, the agent
 * is never blocked; the human sees the warning in the log.
 */
export function makeFirstContactGate(
  kind: "share" | "handoff",
  deps: ShareConsentDeps = {},
): (recipients: Array<{ member: string; identityPub: string }>) => Promise<boolean> {
  const out = deps.out ?? ((line: string) => console.log(line));
  const err = deps.err ?? ((line: string) => console.error(line));
  const isTTY = deps.isTTY ?? Boolean(process.stdout.isTTY && process.stdin.isTTY);
  const confirm = deps.confirm ?? readlineConfirm;
  return async (recipients) => {
    const lines = recipients.map(
      (r) => `      · ${r.member}  (key ${r.identityPub.slice(0, 12)}…, never verified)`,
    );
    if (!isTTY) {
      err(
        `Warning: this ${kind} includes ${recipients.length} first-contact recipient${recipients.length === 1 ? "" : "s"} you have never verified:`,
      );
      for (const l of lines) err(l);
      err("  Proceeding (TOFU). Run `aethereum verify` to pin + verify them for compromise resistance.");
      return true;
    }
    out("");
    out("  ⚠ UNVERIFIED RECIPIENTS — these members have never been pinned or verified:");
    out("");
    for (const l of lines) out(l);
    out("");
    out("  A compromised server could have injected a member here. If you expected");
    out("  everyone on this list, you can proceed; to make this room compromise-");
    out("  resistant, run `aethereum verify` and compare the safety number out of band.");
    out("");
    return confirm(`  Encrypt for them anyway? [y/N] `);
  };
}

function getFlag(name: string, args: string[]): string | undefined {
  const idx = args.indexOf(`--${name}`);
  if (idx === -1) return undefined;
  const value = args[idx + 1];
  if (value === undefined || value.startsWith("--")) return undefined;
  return value;
}

/** A stable logical ref for a file: its path relative to the project root, or basename. */
function refForFile(file: string, cwd: string): string {
  const abs = isAbsolute(file) ? file : `${cwd}/${file}`;
  const rel = relative(cwd, abs);
  return rel && !rel.startsWith("..") ? rel : basename(file);
}

function resolveClient(cwd: string): HostedCodeClient | { error: string } {
  const proj = resolveProject(cwd);
  if (!proj?.url || !proj?.token) {
    return { error: "No room is configured here. Run `aethereum init` or `aethereum join <code>` first." };
  }
  if (!proj.roomId) {
    return { error: "This room has no room id on record; re-run `aethereum join <code>` to refresh it." };
  }
  return new HostedCodeClient(proj.url, proj.token, proj.roomId);
}

export async function runShare(
  args: string[],
  cwd: string = process.cwd(),
  deps: ShareConsentDeps = {},
): Promise<number> {
  const file = args.find((a) => !a.startsWith("--"));
  if (!file) {
    console.error("usage: aethereum share <file> [--as <ref>] [--contract <name>] [--yes]");
    return 1;
  }
  const client = resolveClient(cwd);
  if ("error" in client) {
    console.error(client.error);
    return 1;
  }
  let plaintext: string;
  try {
    plaintext = readFileSync(file, "utf8");
  } catch {
    console.error(`Cannot read ${file}.`);
    return 1;
  }
  const ref = getFlag("as", args) ?? refForFile(file, cwd);
  const contract = getFlag("contract", args);
  const skipConsent = args.includes("--yes");
  try {
    const res = await client.shareCodePlain({
      ref,
      plaintext,
      contract,
      onFirstContact: skipConsent ? undefined : makeFirstContactGate("share", deps),
    });
    console.log(
      `Shared "${ref}" end-to-end encrypted with ${res.recipients} ${res.recipients === 1 ? "member" : "members"}. The server stored only ciphertext; it can never read this code.`,
    );
    if (res.dropped > 0) {
      console.log(
        `Note: ${res.dropped} directory ${res.dropped === 1 ? "entry was" : "entries were"} dropped (wrap key not validly signed) and did NOT receive the code.`,
      );
    }
    if (res.excluded.length > 0) {
      console.log(
        `Note: this room requires verified recipients; ${res.excluded.length} unverified ${res.excluded.length === 1 ? "member was" : "members were"} excluded (${res.excluded.join(", ")}). Run \`aethereum verify\` with them to include them.`,
      );
    }
    if (res.policyDegraded) {
      console.log("Note: the room's require-verified policy could not be read; treated as off for this share.");
    }
    return 0;
  } catch (e) {
    console.error(`Share failed: ${(e as Error).message}`);
    return 1;
  }
}

export async function runFetch(args: string[], cwd: string = process.cwd()): Promise<number> {
  const ref = args.find((a) => !a.startsWith("--"));
  if (!ref) {
    console.error("usage: aethereum fetch <ref> [--out <file>] [--id <artifactId>]");
    return 1;
  }
  const client = resolveClient(cwd);
  if ("error" in client) {
    console.error(client.error);
    return 1;
  }
  const id = getFlag("id", args);
  const out = getFlag("out", args);
  try {
    const got = await client.fetchCodePlain({ ref, id });
    if (!got) {
      console.log(`No shared code found for "${ref}" in this room.`);
      return 0;
    }
    const provenance = got.authorVerified ? "author signature verified" : "author UNVERIFIED";
    // v6-S21: shared code is authored by another principal. Scan it client-side
    // (after decryption) and warn on injection shapes before it is written/printed.
    const matches = scanForInjection(got.plaintext);
    if (matches.length > 0) {
      console.error("");
      console.error(injectionWarning(matches, got.authorMember.slice(0, 8)));
      console.error("");
    }
    if (out) {
      writeFileSync(out, got.plaintext);
      console.log(`Wrote ${out} — "${ref}" v${got.version} (${provenance}). Treat shared code as untrusted until you have reviewed it.`);
    } else {
      process.stdout.write(got.plaintext);
      if (!got.plaintext.endsWith("\n")) process.stdout.write("\n");
      console.error(`# "${ref}" v${got.version} (${provenance}, from ${got.authorMember}) — review before running; shared code is untrusted.`);
    }
    return 0;
  } catch (e) {
    // A tamper / withheld-wrap / downgrade failure is a HARD error — surface it, never
    // fall back to plaintext.
    console.error(`Fetch failed: ${(e as Error).message}`);
    return 1;
  }
}

/**
 * `aethereum verify [--confirm]` — the out-of-band safety-number check that upgrades a
 * room from passive-secure (ciphertext only) to compromise-resistant. Shows the room's
 * safety number (a fingerprint over every member's identity key); two members compare
 * it over a channel that does NOT go through the server (in person, a call, a QR). If
 * it matches, `--confirm` pins the keys + marks the room verified — after which any
 * later server key substitution is a hard block on sharing.
 */
export async function runVerify(args: string[], cwd: string = process.cwd()): Promise<number> {
  const proj = resolveProject(cwd);
  if (!proj?.url || !proj?.token || !proj?.roomId) {
    console.error("No room is configured here. Run `aethereum join <code>` or `aethereum init` first.");
    return 1;
  }
  const client = new HostedCodeClient(proj.url, proj.token, proj.roomId);
  let directory;
  try {
    // Make sure our own key is published so the safety number covers us too.
    await client.provision();
    directory = await client.memberPublicKeys();
  } catch (e) {
    console.error(`Could not load the room directory: ${(e as Error).message}`);
    return 1;
  }

  const valid = directory.filter(validRosterEntry);
  if (valid.length === 0) {
    console.error("No validly-signed members in this room yet. Ask a teammate to join, then retry.");
    return 1;
  }

  const members = valid.map((e) => ({ member: e.member, identityPub: unb64(e.identityPub) }));
  const safety = verifyMod.safetyNumber(members);

  const store = loadVerifyStore(proj.roomId);
  const stored = store.pins.map((p) => ({ member: p.member, identityPub: unb64(p.identityPub) }));
  const diverged = verifyMod.detectPinDivergence(stored, members).filter((d) => d.changed);

  console.log(`\n  Room safety number (${valid.length} member${valid.length === 1 ? "" : "s"}):\n`);
  console.log(`      ${safety}\n`);
  console.log("  Compare this with a teammate over a channel that does NOT go through");
  console.log("  Aethereum (in person, a call, or a QR scan). If it matches on both sides,");
  console.log("  the server cannot be substituting keys.\n");

  if (diverged.length > 0) {
    console.log("  ⚠ WARNING: the identity key for these members CHANGED since you last verified:");
    for (const d of diverged) console.log(`      · ${d.member}`);
    console.log("  If you did not expect this, the server may be substituting keys. Do NOT");
    console.log("  confirm unless you have re-checked the safety number out of band.\n");
  }

  const confirm = args.includes("--confirm");
  if (!confirm) {
    console.log("  Once you have compared it and it matches, run:  aethereum verify --confirm\n");
    return 0;
  }

  saveVerifyStore({
    version: 1,
    roomId: proj.roomId,
    pins: valid.map((e) => ({ member: e.member, identityPub: e.identityPub })),
    verified: valid.map((e) => e.member),
    safetyNumber: safety,
    verifiedAt: Date.now(),
  });
  console.log("  ✓ Room verified. Identity keys pinned; a later key substitution will now");
  console.log("    hard-block sharing until you re-verify. This room is compromise-resistant.\n");
  return 0;
}
